// ─────────────────────────────────────────────────────────────
// ÉCONOMIE DE L'OASIS
// Toutes les valeurs chiffrées du jeu vivent ici. Un seul fichier
// à toucher pour rééquilibrer.
//
// Hypothèse de calibrage :
//   Daily plan (50) + 2 leçons (60) + ~40 bonnes réponses avec
//   multiplicateurs (~90)  ≈  200 Dinars / jour pour un joueur assidu.
// ─────────────────────────────────────────────────────────────

import { t } from "@/i18n";

export const EARN = {
  QUESTION_CLASSIQUE: 1,
  QUESTION_FAUSSE: 0,
  LECON_VALIDEE: 30,
  DAILY_PLAN_COMPLETED: 50,
  MULT_MINI_JEU_NORMAL: 1,
  MULT_MINI_JEU_ERREURS: 2,
  MULT_MINI_JEU_REVISION: 3,
};

// ── Raretés ───────────────────────────────────────────────────
export const RARITIES = {
  commun: {
    id: "commun",
    get label() {
      return t("economy.label_banal");
    },
    order: 0,
    color: "#8e877b",
    ring: "ring-stone-400",
    chip: "bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300",
    glow: "from-stone-300/40",
    price: 60, // achetable en boutique
  },
  peu_commun: {
    id: "peu_commun",
    get label() {
      return t("economy.label_courant");
    },
    order: 1,
    color: "#3f9e6a",
    ring: "ring-emerald-400",
    chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300",
    glow: "from-emerald-300/40",
    price: 190,
  },
  rare: {
    id: "rare",
    get label() {
      return t("economy.label_rare");
    },
    order: 2,
    color: "#3b7fd4",
    ring: "ring-sky-400",
    chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300",
    glow: "from-sky-300/50",
    price: 620,
  },
  epique: {
    id: "epique",
    get label() {
      return t("economy.label_epique");
    },
    order: 3,
    color: "#8b5cf6",
    ring: "ring-violet-400",
    chip: "bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300",
    glow: "from-violet-400/50",
    price: 1500, // valeur de référence — NON achetable
    boxOnly: true,
  },
  legendaire: {
    id: "legendaire",
    get label() {
      return t("economy.label_legendaire");
    },
    order: 4,
    color: "#f59e0b",
    ring: "ring-amber-400",
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300",
    glow: "from-amber-400/60",
    price: 4000, // valeur de référence — NON achetable
    boxOnly: true,
  },
};

export const RARITY_ORDER = [
  "commun",
  "peu_commun",
  "rare",
  "epique",
  "legendaire",
];

// Rareté à part : les habitants.
RARITIES.recompense = {
  id: "recompense",
  get label() {
    return t("economy.label_confrerie");
  },
  order: 5,
  color: "#e0873f",
  ring: "ring-orange-400",
  chip: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300",
  glow: "from-orange-400/50",
  price: 0,
  boxOnly: false,
  reward: true,
};

// Prix d'un objet : sa surcharge si elle existe, sinon celui de sa rareté.
export const priceOf = (item) =>
  item?.price ?? RARITIES[item?.rarity]?.price ?? 0;

// ── Habitants supplémentaires ─────────────────────────────────
// Le PREMIER habitant reste gratuit : c'est la récompense de la
// confrérie, et elle doit le rester. En revanche, une fois la
// confrérie complétée, on peut en installer autant qu'on veut sur
// l'île — chacun au même prix modeste. Volontairement bon marché :
// c'est de la décoration vivante, pas un objet de collection.
export const NPC_PRICE = 200;

// Chaque exemplaire supplémentaire coûte plus cher que le précédent.
// Raison : sans palier croissant, le prix modeste qui rend le deuxième
// habitant attirant rend aussi le vingtième trivial — on se retrouve
// avec des îles couvertes de clones sans que ça ait rien coûté. La
// progression est douce (×1,55) : le 2ᵉ reste une décision facile, le
// 6ᵉ devient un vrai choix.
//   offert · 200 · 310 · 480 · 745 · 1 155 · 1 790 …
export const NPC_GROWTH = 1.55;

// `owned` = nombre d'exemplaires DÉJÀ possédés (le premier est offert
// par la confrérie, il ne compte donc pas comme un achat).
export function npcPriceFor(owned = 1) {
  const paid = Math.max(0, (owned || 0) - 1); // achats déjà faits
  return Math.round((NPC_PRICE * Math.pow(NPC_GROWTH, paid)) / 5) * 5;
}

// Coût total de `qty` exemplaires d'affilée, chacun plus cher que le
// précédent — c'est ce total qu'affiche le compteur de quantité.
export function npcPriceTotal(owned = 1, qty = 1) {
  let sum = 0;
  for (let i = 0; i < qty; i++) sum += npcPriceFor(owned + i);
  return sum;
}

export const npcPriceOf = (item) => item?.price || NPC_PRICE;

// Revente d'un doublon : 30 % du prix, arrondi à 5.
export const RESALE_RATIO = 0.3;
export const resaleValue = (item) =>
  item?.resale ??
  Math.max(5, Math.round((priceOf(item) * RESALE_RATIO) / 5) * 5);

// ── Coffres ───────────────────────────────────────────────────
export const BOXES = [
  {
    id: "souk",
    get name() {
      return t("economy.coffre_souk_name");
    },
    get tag() {
      return t("economy.coffre_souk_tag");
    },
    price: 150,
    accent: "from-stone-400 to-stone-600",
    odds: {
      commun: 0.62,
      peu_commun: 0.28,
      rare: 0.085,
      epique: 0.013,
      legendaire: 0.002,
    },
  },
  {
    id: "oasis",
    get name() {
      return t("economy.coffre_oasis_name");
    },
    get tag() {
      return t("economy.coffre_oasis_tag");
    },
    price: 420,
    accent: "from-emerald-400 to-teal-600",
    odds: {
      commun: 0.25,
      peu_commun: 0.42,
      rare: 0.26,
      epique: 0.06,
      legendaire: 0.01,
    },
  },
  {
    id: "sultan",
    get name() {
      return t("economy.coffre_sultan_name");
    },
    get tag() {
      return t("economy.coffre_sultan_tag");
    },
    price: 1300,
    accent: "from-violet-500 to-amber-500",
    odds: {
      commun: 0,
      peu_commun: 0,
      rare: 0.55,
      epique: 0.38,
      legendaire: 0.07,
    },
  },
];

// Pitié : au bout de 25 coffres sans Épique+, le prochain est garanti Épique.
export const PITY_THRESHOLD = 25;

// ── Chunks (parcelles d'île) ──────────────────────────────────
export const CHUNK_TILES = 6; // 6×6 tuiles par parcelle
export const STARTING_CHUNKS = [
  // on démarre avec 3 parcelles en L
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
];

export const MAX_CHUNKS = 36;
const CHUNK_BASE = 250;
const CHUNK_GROWTH = 1.1;

export function chunkPrice(owned) {
  if (owned >= MAX_CHUNKS) return null;
  const k = owned - STARTING_CHUNKS.length;
  return Math.round((CHUNK_BASE * Math.pow(CHUNK_GROWTH, k)) / 5) * 5;
}

// Paliers affichés au joueur, pour donner un cap à moyen terme.
export const CHUNK_TIERS = [
  {
    at: 6,
    get label() {
      return t("economy.tier_hameau");
    },
  },
  {
    at: 12,
    get label() {
      return t("economy.tier_village");
    },
  },
  {
    at: 20,
    get label() {
      return t("economy.tier_bourg");
    },
  },
  {
    at: 28,
    get label() {
      return t("economy.tier_cite");
    },
  },
  {
    at: 36,
    get label() {
      return t("economy.tier_archipel");
    },
  },
];
export const tierFor = (n) =>
  CHUNK_TIERS.find((item) => n < item.at) ||
  CHUNK_TIERS[CHUNK_TIERS.length - 1];

// ── Tirage ────────────────────────────────────────────────────
export function rollRarity(box, pityCount) {
  if (pityCount >= PITY_THRESHOLD) {
    return Math.random() < 0.12 ? "legendaire" : "epique";
  }
  let r = Math.random();
  for (const key of RARITY_ORDER) {
    const p = box.odds[key] || 0;
    if (r < p) return key;
    r -= p;
  }
  return "commun";
}

export function pickFromPool(pool) {
  return pool[Math.floor(Math.random() * pool.length)];
}

// Formatage court : 1 240 → « 1 240 »
export const fmt = (n) =>
  new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));
